import React, { useEffect, useState } from "react";
import { useApp } from "@/lib/store";
import api from "@/lib/api";
import {
    Loader2,
    Sparkles,
    EyeOff,
    Trash2,
    Flag,
    Filter,
    MessageSquare,
    Info,
    RefreshCw,
    ExternalLink,
    Instagram,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const CLASS_STYLES = {
    Positive: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
    Question: "border-cyan-500/30 bg-cyan-500/10 text-cyan-200",
    Complaint: "border-amber-500/30 bg-amber-500/10 text-amber-200",
    Normal: "border-white/10 bg-white/5 text-slate-300",
    Spam: "border-rose-500/30 bg-rose-500/10 text-rose-200",
    Abusive: "border-rose-500/30 bg-rose-500/10 text-rose-200",
    "Hate-harassment": "border-rose-500/30 bg-rose-500/10 text-rose-200",
    "Sexual-inappropriate": "border-rose-500/30 bg-rose-500/10 text-rose-200",
    "Irrelevant-promotion": "border-white/10 bg-white/5 text-slate-400",
};

const CLASSES = [
    "Normal", "Positive", "Question", "Complaint", "Spam",
    "Abusive", "Hate-harassment", "Sexual-inappropriate", "Irrelevant-promotion",
];

const REPLY_POLICIES = [
    { v: "ignore", l: "Ignore" },
    { v: "approval", l: "Require approval" },
    { v: "autopilot", l: "Autopilot" },
];
const MOD_POLICIES = [
    { v: "ignore", l: "Ignore" },
    { v: "flag", l: "Flag only" },
    { v: "auto_hide", l: "Auto-hide" },
    { v: "auto_delete", l: "Auto-delete" },
];

export default function Comments() {
    const { currentBusiness } = useApp();
    const [comments, setComments] = useState([]);
    const [mod, setMod] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterClass, setFilterClass] = useState("all");
    const [busy, setBusy] = useState(null);
    const [igConn, setIgConn] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const live = igConn?.status === "connected" && igConn?.mode === "live";

    const load = async () => {
        if (!currentBusiness) return;
        setLoading(true);
        try {
            const params = filterClass !== "all" ? { classification: filterClass } : {};
            const [{ comments: c }, { moderation: m }, ints] = await Promise.all([
                api.igComments(currentBusiness.id, params),
                api.commentModeration(currentBusiness.id),
                api.integrations(currentBusiness.id).catch(() => null),
            ]);
            setComments(c || []);
            setMod(m);
            setIgConn((ints?.integrations || []).find((i) => i.provider === "instagram") || null);
        } catch {
            toast.error("Couldn't load comments.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBusiness?.id, filterClass]);

    const genReply = async (id) => {
        setBusy(`reply-${id}`);
        try {
            const r = await api.commentGenReply(id);
            if (r.status === "pending_approval")
                toast.success("Reply drafted → Approval Inbox.");
            else if (r.status === "published")
                toast.success(r.demo_label || "Reply published.");
            else toast.info(r.reason || "Skipped.");
            load();
        } catch {
            toast.error("Failed to generate reply.");
        } finally {
            setBusy(null);
        }
    };

    const doAction = async (id, action) => {
        setBusy(`${action}-${id}`);
        setConfirmDelete(null);
        try {
            const r = await api.commentAction(id, action);
            toast.success(r.label || `${action} applied.`);
            load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Action failed.");
        } finally {
            setBusy(null);
        }
    };

    const toggleLiveAuto = async (on) => {
        setMod((m) => ({ ...m, live_auto: on }));
        try {
            const r = await api.setLiveAutoModeration(currentBusiness.id, on);
            setMod(r.moderation);
            toast.success(on ? "Rules now apply to new Instagram comments." : "Automatic moderation off.");
        } catch {
            setMod((m) => ({ ...m, live_auto: !on }));
            toast.error("Update failed.");
        }
    };

    const syncNow = async () => {
        setSyncing(true);
        try {
            const r = await api.syncIntegration(currentBusiness.id, "instagram");
            toast.success(
                r.new
                    ? `${r.new} new comment${r.new === 1 ? "" : "s"} from Instagram.`
                    : "Instagram is up to date.",
            );
            load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(
                d === "instagram_token_expired"
                    ? "Instagram login expired — reconnect it in Settings → Integrations."
                    : typeof d === "string" ? d : "Sync failed.",
            );
        } finally {
            setSyncing(false);
        }
    };

    const updateMod = async (klass, key, value) => {
        const newRules = {
            ...(mod?.rules || {}),
            [klass]: { ...(mod?.rules?.[klass] || {}), [key]: value },
        };
        setMod({ ...mod, rules: newRules });
        try {
            const r = await api.updateCommentModeration(currentBusiness.id, newRules);
            toast.success(
                r?.applied_to_existing
                    ? `${klass} → ${key}: ${value} · applied to ${r.applied_to_existing} sample comment${r.applied_to_existing === 1 ? "" : "s"}`
                    : `${klass} → ${key}: ${value}`,
            );
            if (r?.applied_to_existing) load();
        } catch {
            toast.error("Update failed.");
        }
    };

    return (
        <div className="space-y-6 fade-in" data-testid="comments-panel">
            {live ? (
                <div
                    className="rounded-xl bg-emerald-500/5 border border-emerald-500/20 p-3 text-xs text-slate-300 flex items-center gap-3 flex-wrap"
                    data-testid="comments-live-banner"
                >
                    <Instagram className="h-4 w-4 text-emerald-300 shrink-0" />
                    <span className="flex-1 min-w-[220px]">
                        Connected to{" "}
                        <span className="font-semibold text-slate-100">
                            @{igConn.account_name}
                        </span>
                        . New comments arrive automatically; replies, hides and deletes on
                        comments marked <span className="text-emerald-300">Live</span> happen on
                        Instagram.
                        {igConn.last_sync_at && (
                            <span className="text-slate-500">
                                {" "}Last sync {new Date(igConn.last_sync_at).toLocaleString()}.
                            </span>
                        )}
                    </span>
                    <Button
                        size="sm"
                        onClick={syncNow}
                        disabled={syncing}
                        data-testid="comments-sync-button"
                        className="h-8 bg-emerald-500/15 border border-emerald-500/30 text-emerald-100 hover:bg-emerald-500/25 gap-1.5"
                    >
                        {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                        Sync now
                    </Button>
                    <label className="w-full flex items-center gap-2 text-[11px] text-slate-400 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={!!mod?.live_auto}
                            onChange={(e) => toggleLiveAuto(e.target.checked)}
                            data-testid="comments-live-auto"
                            className="accent-emerald-500"
                        />
                        Apply the rules below automatically to new Instagram comments (automatic
                        rules hide instead of delete, so they can be undone). Off: comments are only
                        sorted and you decide.
                    </label>
                </div>
            ) : (
                <div className="rounded-xl bg-white/[0.02] border border-white/5 p-3 text-xs text-slate-400 flex items-start gap-2">
                    <Info className="h-3.5 w-3.5 mt-0.5 text-cyan-400 shrink-0" />
                    <span>
                        Sample comments — Instagram isn't connected for this business, so
                        hide, delete and reply only change these samples. Connect Instagram in
                        Settings → Integrations to moderate your real comments here.
                    </span>
                </div>
            )}

            {/* Moderation rules */}
            <div className="surface rounded-2xl p-5" data-testid="comment-moderation-panel">
                <div className="label-eyebrow text-indigo-300 mb-3">
                    Per-class moderation rules
                </div>
                <div className="space-y-2">
                    {CLASSES.map((klass) => (
                        <div
                            key={klass}
                            data-testid={`comment-mod-row-${klass}`}
                            className="grid grid-cols-1 md:grid-cols-3 gap-2 py-1"
                        >
                            <div className="text-sm font-semibold text-slate-200 flex items-center">
                                <Badge variant="outline" className={`${CLASS_STYLES[klass]}`}>
                                    {klass}
                                </Badge>
                            </div>
                            <Select
                                value={mod?.rules?.[klass]?.reply || "ignore"}
                                onValueChange={(v) => updateMod(klass, "reply", v)}
                            >
                                <SelectTrigger
                                    data-testid={`comment-reply-policy-${klass}`}
                                    className="bg-white/[0.03] border-white/10 text-sm text-slate-100 h-9"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {REPLY_POLICIES.map((o) => (
                                        <SelectItem key={o.v} value={o.v}>
                                            Reply: {o.l}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select
                                value={mod?.rules?.[klass]?.moderation || "ignore"}
                                onValueChange={(v) => updateMod(klass, "moderation", v)}
                            >
                                <SelectTrigger
                                    data-testid={`comment-mod-policy-${klass}`}
                                    className="bg-white/[0.03] border-white/10 text-sm text-slate-100 h-9"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {MOD_POLICIES.map((o) => (
                                        <SelectItem key={o.v} value={o.v}>
                                            Mod: {o.l}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    ))}
                </div>
            </div>

            {/* Filter */}
            <div className="flex items-center gap-3">
                <Filter className="h-4 w-4 text-slate-500" />
                <Select value={filterClass} onValueChange={setFilterClass}>
                    <SelectTrigger
                        data-testid="comment-filter-classification"
                        className="w-56 bg-white/[0.03] border-white/10 text-slate-200 text-xs h-9"
                    >
                        <SelectValue placeholder="Class" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All classes</SelectItem>
                        {CLASSES.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <span className="text-xs text-slate-500 mono">{comments.length} comments</span>
            </div>

            {loading ? (
                <div className="text-center py-16 text-slate-500">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                </div>
            ) : (
                <div className="space-y-3">
                    {comments.map((c) => (
                        <div
                            key={c.id}
                            data-testid={`comment-card-${c.id}`}
                            className="surface rounded-2xl p-4"
                        >
                            <div className="flex items-start gap-3">
                                {c.author_avatar ? (
                                    <img
                                        src={c.author_avatar}
                                        alt=""
                                        className="h-8 w-8 rounded-full object-cover border border-white/10 shrink-0"
                                    />
                                ) : (
                                    <div className="h-8 w-8 rounded-full border border-white/10 bg-white/5 shrink-0 flex items-center justify-center text-[11px] font-semibold text-slate-300 uppercase">
                                        {(c.author || "?").slice(0, 1)}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-semibold text-slate-100">
                                            @{c.author}
                                        </span>
                                        <Badge
                                            variant="outline"
                                            className={CLASS_STYLES[c.classification]}
                                            data-testid={`comment-class-${c.id}`}
                                        >
                                            {c.classification}
                                        </Badge>
                                        <span className="text-[11px] text-slate-500 mono uppercase">
                                            {c.sentiment}
                                        </span>
                                        {c.source === "instagram" ? (
                                            <Badge
                                                variant="outline"
                                                className="border-emerald-500/30 text-emerald-300"
                                            >
                                                Live
                                            </Badge>
                                        ) : (
                                            <span className="text-[10px] mono uppercase tracking-wider text-slate-600">
                                                sample
                                            </span>
                                        )}
                                        {c.permalink && (
                                            <a
                                                href={c.permalink}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-slate-500 hover:text-slate-300"
                                                title="Open the post on Instagram"
                                            >
                                                <ExternalLink className="h-3 w-3" />
                                            </a>
                                        )}
                                        {c.status !== "visible" && (
                                            <Badge
                                                className="bg-rose-500/15 border border-rose-500/30 text-rose-200"
                                                data-testid={`comment-status-${c.id}`}
                                            >
                                                {c.status}
                                            </Badge>
                                        )}
                                        {c.reply_status === "pending" && (
                                            <Badge className="bg-amber-500/15 border border-amber-500/30 text-amber-200">
                                                pending approval
                                            </Badge>
                                        )}
                                        {c.reply_status === "published" && (
                                            <Badge className="bg-emerald-500/15 border border-emerald-500/30 text-emerald-200">
                                                published
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="mt-1 text-sm text-slate-200">{c.text}</p>
                                    {c.demo_action_note && (
                                        <div className="mt-1 text-[10px] mono text-slate-500">
                                            {c.demo_action_note}
                                        </div>
                                    )}
                                    {c.published_reply && (
                                        <div className="mt-2 text-xs text-emerald-200 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-2">
                                            <span className="mono uppercase text-[10px] tracking-widest text-emerald-400 block mb-1">
                                                Reply
                                            </span>
                                            {c.published_reply}
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col gap-1.5 shrink-0">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => genReply(c.id)}
                                        disabled={busy === `reply-${c.id}` || c.reply_status === "pending"}
                                        data-testid={`comment-generate-reply-${c.id}`}
                                        className="h-8 border-indigo-500/30 bg-indigo-500/10 text-indigo-200 hover:bg-indigo-500/20 gap-1"
                                    >
                                        {busy === `reply-${c.id}` ? (
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                            <Sparkles className="h-3 w-3" />
                                        )}
                                        Reply
                                    </Button>
                                    <div className="flex gap-1">
                                        <IconBtn
                                            testid={`comment-flag-${c.id}`}
                                            onClick={() => doAction(c.id, "flag")}
                                            disabled={busy === `flag-${c.id}`}
                                            title="Flag"
                                        >
                                            <Flag className="h-3 w-3" />
                                        </IconBtn>
                                        <IconBtn
                                            testid={`comment-hide-${c.id}`}
                                            onClick={() => doAction(c.id, "hide")}
                                            disabled={busy === `hide-${c.id}`}
                                            title={c.source === "instagram" ? "Hide on Instagram" : "Hide (sample)"}
                                        >
                                            <EyeOff className="h-3 w-3" />
                                        </IconBtn>
                                        <IconBtn
                                            testid={`comment-delete-${c.id}`}
                                            onClick={() =>
                                                c.source === "instagram" && confirmDelete !== c.id
                                                    ? setConfirmDelete(c.id)
                                                    : doAction(c.id, "delete")
                                            }
                                            disabled={busy === `delete-${c.id}`}
                                            danger={confirmDelete === c.id}
                                            title={
                                                c.source !== "instagram"
                                                    ? "Delete (sample)"
                                                    : confirmDelete === c.id
                                                      ? "Click again to delete on Instagram — this can't be undone"
                                                      : "Delete on Instagram"
                                            }
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </IconBtn>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function IconBtn({ children, testid, danger, ...props }) {
    return (
        <button
            type="button"
            {...props}
            aria-label={props.title}
            data-testid={testid}
            className={`h-8 w-8 rounded-lg border flex items-center justify-center disabled:opacity-40 ${
                danger
                    ? "border-rose-500/50 bg-rose-500/20 text-rose-100"
                    : "border-white/10 bg-white/5 hover:bg-white/10 text-slate-300"
            }`}
        >
            {children}
        </button>
    );
}
